import { OverlayKeyboardDispatcher, OverlayRef } from '@angular/cdk/overlay';
import { Injectable, NgZone } from '@angular/core';

type KeydownListener = (event: KeyboardEvent) => void;

/**
 * Klávesy pro plovoucí vrstvy CDK poslouchané na `<html>`, ne na `<body>` (EL-2293).
 *
 * ⚠ Plovoucí vrstvy od EL-2271 visí pod `<html>` (`HtmlOverlayContainerService`), ale
 * CDK čte klávesy pro ně na `<body>`. Klávesa stisknutá **uvnitř** vrstvy — v nabídce,
 * v potvrzení, v dialogu, v zásuvce — tak k posluchači nedoputovala vůbec a Escape
 * nezavřel nic, dokud byl fokus uvnitř. Myší to nešlo poznat; z klávesnice se z otevřené
 * nabídky nedalo odejít jinak než Tabem mimo ni.
 *
 * Posluchač je tentýž (CDK ho má jako soukromé pole), jen visí o úroveň výš: na `<html>`
 * dojde klávesa z `<body>` i z kontejneru vrstev. Přidává se mimo zónu Angularu jako
 * v CDK — do zóny vstupuje až vrstva, která klávesu opravdu odebírá.
 *
 * ⚠ **Přesouvá se po každém `add`, ne jen po prvním.** `nz-drawer` při otevření znovu
 * přidá overlay, který v seznamu už je (`NzDrawerComponent.open`). CDK ho napřed odebere,
 * seznam se tím na okamžik vyprázdní, posluchač se odpojí — a CDK ho pak připojí znovu,
 * **na `<body>`**. Dokud se přesouvalo jen při přechodu „nepřipojeno → připojeno",
 * zůstal po otevření zásuvky na `<body>` a Escape v mobilní osnově kurzu nezavřel nic
 * (křížek ano). `addEventListener` s týmž posluchačem je idempotentní, takže opakovaný
 * přesun nic nezdvojí.
 *
 * ⚠ **Z `<body>` se odpojuje úklidovou funkcí CDK, ne `removeEventListener`** (EL-2338).
 * Od CDK 19 se posluchač připojuje přes renderer, který ho obalí — `removeEventListener`
 * s holou funkcí pak na `<body>` nic nenajde a klávesa dorazí do vrstvy dvakrát.
 */
@Injectable()
export class HtmlOverlayKeyboardDispatcher extends OverlayKeyboardDispatcher {

  override add(overlayRef: OverlayRef): void {
    super.add(overlayRef);

    if (this._isAttached) {
      const listener = this.listener;

      const root = this._document.documentElement;

      const cdk = this.cdk;
      cdk._cleanupKeydown?.();
      cdk._cleanupKeydown = undefined;

      if (this.zone) {
        this.zone.runOutsideAngular(() => root.addEventListener('keydown', listener));
      } else {
        root.addEventListener('keydown', listener);
      }
    }
  }

  protected override detach(): void {
    if (this._isAttached) {
      this._document.documentElement.removeEventListener('keydown', this.listener);
    }

    super.detach();
  }

  /** Posluchač z CDK — soukromé pole předka, proto přes index. */
  private get listener(): KeydownListener {
    return (this as unknown as { _keydownListener: KeydownListener })._keydownListener;
  }

  /** Úklid posluchače na `<body>` — soukromé pole předka (CDK 19+). */
  private get cdk(): { _cleanupKeydown?: () => void } {
    return this as unknown as { _cleanupKeydown?: () => void };
  }

  private get zone(): NgZone | undefined {
    return (this as unknown as { _ngZone?: NgZone })._ngZone;
  }
}
